import { Color, Entity, EventHandle, Quat, Vec3 } from 'playcanvas';

import { Element, ElementType } from './element';
import { Serializer } from './serializer';

const tmpA = new Vec3();
const tmpB = new Vec3();

class HelperPoint extends Element {
    name: string;
    entity: Entity;

    // Allow Selection system to treat this like other selectable elements.
    private _visible = true;

    vehicleId: string | number | null = null;
    groupId: string | null = null;

    // True when driven by imported data. Animated helpers are transform-locked.
    animated = false;

    // Draw helper axes when selected, and optionally when globally toggled on.
    private _selected = false;
    private _showGizmos = false;

    // Optional scene graph parent for this helper entity.
    // If omitted, helper is added directly under scene.contentRoot.
    parentEntity: Entity | null = null;

    private _selectionHandle: EventHandle | null = null;
    private _gizmosHandle: EventHandle | null = null;

    constructor(args: {
        name: string;
        animated?: boolean;
        visible?: boolean;
        vehicleId?: string | number | null;
        groupId?: string | null;
        parentEntity?: Entity | null;
    }) {
        super(ElementType.helper);

        this.name = args.name;
        this.animated = !!args.animated;
        if (typeof args.visible === 'boolean') {
            this.visible = args.visible;
        }
        if (args.vehicleId !== undefined) {
            this.vehicleId = args.vehicleId;
        }
        if (args.groupId !== undefined) {
            this.groupId = args.groupId;
        }

        if (args.parentEntity !== undefined) {
            this.parentEntity = args.parentEntity;
        }

        this.entity = new Entity(this.name);
        this.entity.enabled = this._visible;
    }

    // Animated helpers are driven by imported data and should not be user-transformable.
    // This is intended to be used by gizmo / UI gating.
    get transformLocked() {
        return this.animated;
    }

    set visible(value: boolean) {
        const next = !!value;
        if (next !== this._visible) {
            this._visible = next;
            this.entity.enabled = next;
            // Make sure the render loop picks this up even if nothing else changes.
            if (this.scene) {
                this.scene.forceRender = true;
                this.scene.events.fire('helper.visibility', this);
            }
        }
    }

    get visible() {
        return this._visible;
    }

    add() {
        (this.parentEntity ?? this.scene.contentRoot).addChild(this.entity);

        // Selection state
        this._selectionHandle = this.scene.events.on('selection.changed', (sel: any) => {
            const wasSelected = this._selected;
            this._selected = sel === this;
            if (this._selected !== wasSelected) {
                this.scene.forceRender = true;
            }
        });

        // Optional gizmo visibility toggle. (No UI yet; safe default is off.)
        this._gizmosHandle = this.scene.events.on('helpers.gizmos', (show: boolean) => {
            this._showGizmos = !!show;
            this.scene.forceRender = true;
        });

        // Initialize from current global state so helpers added after toggling inherit the setting.
        const initialShow = this.scene.events.invoke('helpers.gizmos');
        if (typeof initialShow === 'boolean') {
            this._showGizmos = initialShow;
        }
    }

    remove() {
        // Maintain logical hierarchy bookkeeping even when helpers are destroyed programmatically.
        // (reparentPreserveWorld handles this when explicitly reparenting.)
        const parent = this.parentElement;
        if (parent) {
            const idx = parent.childElements.indexOf(this);
            if (idx !== -1) parent.childElements.splice(idx, 1);
            this.parentElement = null;
        }

        // Detach children from this helper in the logical hierarchy.
        // NOTE: We don't automatically reparent entities here; callers doing structured deletions
        // should unparent meshes/splats explicitly (see helper-list bundle deletion).
        if (this.childElements.length > 0) {
            for (const ch of this.childElements) {
                if (ch && ch.parentElement === this) {
                    ch.parentElement = null;
                }
            }
            this.childElements.length = 0;
        }

        this._selectionHandle?.off();
        this._selectionHandle = null;

        this._gizmosHandle?.off();
        this._gizmosHandle = null;

        this.entity.parent?.removeChild(this.entity);
        this.entity.destroy();
    }

    serialize(serializer: Serializer) {
        // Important for SceneState diffing: without this, helper transforms changing (eg animation)
        // won't necessarily trigger a render when the rest of the scene is static.
        serializer.packa(this.entity.getWorldTransform().data);
        serializer.pack(this._visible, this.animated, this._selected, this._showGizmos);
    }

    move(position?: Vec3, rotation?: Quat, scale?: Vec3) {
        if (this.animated) {
            // Animation owns animated helper transforms; suppress manual edits.
            return;
        }

        if (position) {
            this.entity.setLocalPosition(position);
        }
        if (rotation) {
            this.entity.setLocalRotation(rotation);
        }
        if (scale) {
            this.entity.setLocalScale(scale);
        }
    }

    onPreRender() {
        this.entity.enabled = this._visible;
        if (!this._visible) return;

        if (!(this._selected || this._showGizmos)) return;

        // Draw a small RGB axis cross at the helper origin.
        // Use short lengths in world space so it stays unobtrusive.
        const len = 0.25;

        const p = this.entity.getPosition();
        const r = this.entity.getRotation();

        r.transformVector(Vec3.RIGHT, tmpA);
        r.transformVector(Vec3.UP, tmpB);

        // X axis (red)
        tmpA.mulScalar(len).add(p);
        this.scene.app.drawLine(p, tmpA, Color.RED, true, this.scene.debugLayer);

        // Y axis (green)
        tmpB.mulScalar(len).add(p);
        this.scene.app.drawLine(p, tmpB, Color.GREEN, true, this.scene.debugLayer);

        // Z axis (blue)
        r.transformVector(Vec3.FORWARD, tmpA);
        tmpA.mulScalar(len).add(p);
        this.scene.app.drawLine(p, tmpA, Color.BLUE, true, this.scene.debugLayer);
    }
}

export { HelperPoint };
